import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, CheckCircle2, X, UserPlus, ChevronLeft, ChevronRight, Edit3, Save, Copy, BookmarkPlus, LayoutTemplate } from "lucide-react";

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const TYPES = ["easy","threshold","tempo","vo2","long","recovery","hill","strength","mobility","race"];
const TYPE_COLORS = {
  easy: "#71c4ff", long: "#d7ff00", threshold: "#ff8800", tempo: "#ff8800",
  vo2: "#ff3b30", hill: "#ff6b00", recovery: "#7fffd4", strength: "#c084fc",
  mobility: "#a3e635", race: "#d7ff00",
};

export default function AdminPrograms() {
  const [progs, setProgs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [view, setView] = useState("programs"); // "programs" | "templates"
  const [library, setLibrary] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [edit, setEdit] = useState(null);
  const [activeWeek, setActiveWeek] = useState(0);
  const [editSession, setEditSession] = useState(null); // {week_index, day_index, session}
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiForm, setAiForm] = useState({ goal: "5k", weeks: 8, weekly_availability_days: 4, notes: "", athlete_id: "", weekly_mileage_target_km: "", required_session_types: [], pace_zones: "", coach_guidance: "", auto_publish_and_assign: false });

  const reloadPrograms = () => api.get("/admin/programs").then(r => setProgs(r.data));
  const reloadTemplates = () => api.get("/admin/templates").then(r => setTemplates(r.data)).catch(() => {});
  const reload = () => { reloadPrograms(); reloadTemplates(); };
  useEffect(() => {
    reload();
    api.get("/workouts/library").then(r => setLibrary(r.data)).catch(() => {});
    api.get("/admin/athletes").then(r => setAthletes(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedAthlete = athletes.find(a => a.id === aiForm.athlete_id);

  const generateAI = async () => {
    setAiBusy(true);
    try {
      const payload = { ...aiForm };
      if (!payload.athlete_id) delete payload.athlete_id;
      if (payload.weekly_mileage_target_km === "" || payload.weekly_mileage_target_km == null) delete payload.weekly_mileage_target_km;
      else payload.weekly_mileage_target_km = Number(payload.weekly_mileage_target_km);
      if (!payload.required_session_types?.length) delete payload.required_session_types;
      if (!payload.pace_zones) delete payload.pace_zones;
      if (!payload.coach_guidance) delete payload.coach_guidance;
      const { data } = await api.post("/admin/ai/generate-program", payload);
      if (data.auto_assigned_to) {
        toast.success(`Program generated, published, and sent to ${data.auto_assigned_to} ✅`);
        setAiOpen(false);
        reload();
      } else {
        toast.success(`AI draft created (${data.sessions?.length || 0} sessions) — review, edit, publish, then assign.`);
        setAiOpen(false);
        reload();
        setEdit({ ...data, sessions: [...(data.sessions || [])] });
        setActiveWeek(0);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed");
    }
    setAiBusy(false);
  };

  const startNew = () => { setEdit({ name: "New Program", goal: "5k", weeks: 8, sessions: [], published: false }); setActiveWeek(0); };
  const startEdit = (p) => { setEdit({ ...p, sessions: [...(p.sessions || [])] }); setActiveWeek(0); };

  const save = async () => {
    try {
      if (edit.id) { await api.patch(`/admin/programs/${edit.id}`, edit); toast.success("Program saved"); }
      else { await api.post("/admin/programs", edit); toast.success("Program created"); }
      setEdit(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete program?")) return;
    await api.delete(`/admin/programs/${id}`);
    reload();
  };

  const saveAsTemplate = async (p) => {
    const name = window.prompt("Template name?", `${p.name} (Template)`);
    if (!name?.trim()) return;
    try {
      await api.post(`/admin/programs/${p.id}/save-as-template`, { name: name.trim() });
      toast.success(`Saved "${name.trim()}" to your template library`);
      reloadTemplates();
      setView("templates");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save template");
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template? (does not affect assigned programs)")) return;
    await api.delete(`/admin/programs/${id}`);
    reloadTemplates();
  };

  const [tplAssignFor, setTplAssignFor] = useState(null); // template object
  const [tplAssignAthleteId, setTplAssignAthleteId] = useState("");
  const [tplAssignBusy, setTplAssignBusy] = useState(false);

  const openTemplateUse = (tpl) => {
    setTplAssignFor(tpl);
    setTplAssignAthleteId("");
  };

  const cloneTemplate = async () => {
    if (!tplAssignFor) return;
    setTplAssignBusy(true);
    try {
      const body = { publish: true };
      if (tplAssignAthleteId) body.athlete_id = tplAssignAthleteId;
      const { data } = await api.post(`/admin/templates/${tplAssignFor.id}/clone`, body);
      if (tplAssignAthleteId) {
        await api.post(`/admin/programs/${data.id}/assign`, { athlete_id: tplAssignAthleteId });
        const ath = athletes.find(a => a.id === tplAssignAthleteId);
        toast.success(`${data.name} assigned to ${ath?.name || "athlete"}`);
      } else {
        toast.success(`${data.name} created from template — review & publish.`);
      }
      setTplAssignFor(null);
      setTplAssignAthleteId("");
      reloadPrograms();
      if (!tplAssignAthleteId) {
        setView("programs");
        setEdit({ ...data, sessions: [...(data.sessions || [])] });
        setActiveWeek(0);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clone template");
    }
    setTplAssignBusy(false);
  };

  // ===== Session editing =====
  const sessionAt = (w, d) => edit?.sessions?.find(s => s.week_index === w && s.day_index === d);
  const upsertSession = (s) => {
    setEdit(e => {
      const filtered = e.sessions.filter(x => !(x.week_index === s.week_index && x.day_index === s.day_index));
      return { ...e, sessions: [...filtered, s] };
    });
  };
  const removeSessionAt = (w, d) => setEdit(e => ({ ...e, sessions: e.sessions.filter(s => !(s.week_index === w && s.day_index === d)) }));

  const openSessionEditor = (w, d) => {
    const existing = sessionAt(w, d);
    setEditSession(existing ? { ...existing } : { week_index: w, day_index: d, name: "", type: "easy", description: "", distance_km: null, duration_min: null, pace_target: "", hr_zone: "" });
  };
  const addFromLibrary = (lib) => {
    if (!editSession) return;
    setEditSession({ ...editSession,
      name: lib.name, type: lib.type, description: lib.description || "",
      distance_km: lib.distance_km, duration_min: lib.duration_min,
      pace_target: lib.pace_target, hr_zone: lib.hr_zone,
    });
  };
  const saveSession = () => {
    if (!editSession.name?.trim()) { toast.error("Session name is required"); return; }
    upsertSession({
      ...editSession,
      distance_km: editSession.distance_km === "" || editSession.distance_km == null ? null : Number(editSession.distance_km),
      duration_min: editSession.duration_min === "" || editSession.duration_min == null ? null : Number(editSession.duration_min),
    });
    setEditSession(null);
  };
  // ADD WEEKS — appends N empty weeks to the END. Existing weeks/sessions are never touched.
  const addWeeks = async (n) => {
    if (edit.id) {
      try {
        const { data } = await api.post(`/admin/programs/${edit.id}/add-weeks`, { count: n });
        setEdit(e => ({ ...e, weeks: data.weeks }));
        setActiveWeek((data.weeks || 1) - 1);
        toast.success(`Added ${n} week${n > 1 ? "s" : ""} — program is now ${data.weeks} weeks`);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not add weeks");
      }
    } else {
      const nw = (edit.weeks || 0) + n;
      setEdit(e => ({ ...e, weeks: nw }));
      setActiveWeek(nw - 1);
      toast.success(`Added ${n} week${n > 1 ? "s" : ""} — ${nw} weeks total`);
    }
  };

  const duplicateWeek = () => {
    if (!confirm(`Duplicate Week ${activeWeek + 1} to next week?`)) return;
    const target = activeWeek + 1;
    if (target >= (edit.weeks || 0)) { toast.error("Increase total weeks first"); return; }
    const source = edit.sessions.filter(s => s.week_index === activeWeek);
    setEdit(e => ({ ...e, sessions: [...e.sessions.filter(s => s.week_index !== target), ...source.map(s => ({ ...s, week_index: target }))] }));
    toast.success(`Copied Week ${activeWeek + 1} → Week ${target + 1}`);
    setActiveWeek(target);
  };

  const monthGroups = useMemo(() => {
    if (!edit) return [];
    const total = edit.weeks || 1;
    const groups = [];
    for (let i = 0; i < total; i += 4) groups.push({ start: i, end: Math.min(i + 4, total) });
    return groups;
  }, [edit]);
  const currentGroup = monthGroups.find(g => activeWeek >= g.start && activeWeek < g.end) || monthGroups[0];

  // ===== Assign modal =====
  const [assignFor, setAssignFor] = useState(null);
  const [assignAthleteId, setAssignAthleteId] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);
  const openAssign = (p) => { setAssignFor(p); setAssignAthleteId(p.target_athlete_id || ""); };
  const doAssign = async () => {
    if (!assignFor || !assignAthleteId) return;
    setAssignBusy(true);
    try {
      const { data } = await api.post(`/admin/programs/${assignFor.id}/assign`, { athlete_id: assignAthleteId });
      toast.success(`${data.program} assigned to ${data.athlete}`);
      setAssignFor(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
    setAssignBusy(false);
  };

  // ===== EDIT VIEW =====
  if (edit) {
    return (
      <div data-testid="admin-program-edit">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">PROGRAM BUILDER</div>
            <input value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} className="mt-1 bg-transparent border-b border-[#222] focus:border-[#d7ff00] outline-none px-1 py-1 font-display text-2xl uppercase tracking-wider min-w-[280px]" data-testid="prog-name" />
          </div>
          <div>
            <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Goal</label>
            <select value={edit.goal} onChange={e => setEdit({ ...edit, goal: e.target.value })} className="block mt-1 bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2" data-testid="prog-goal">
              {["beginner","5k","10k","half","marathon"].map(g => <option key={g} value={g}>{g.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Total weeks</label>
            {!edit.id ? (
              <select value={edit.weeks} onChange={e => setEdit({ ...edit, weeks: Number(e.target.value) })} className="block mt-1 bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2" data-testid="prog-weeks">
                {[4, 6, 8, 10, 12, 16].map(w => <option key={w} value={w}>{`${w} weeks`}</option>)}
              </select>
            ) : (
              <div className="mt-1 px-3 py-2 bg-[#0e0e0e] border border-[#222] font-display text-lg text-[#d7ff00] leading-none" data-testid="prog-weeks-total">{edit.weeks} <span className="font-mono-jp text-[10px] text-[#888]">WEEKS</span></div>
            )}
          </div>
          <div>
            <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Add weeks →</label>
            <div className="mt-1 flex gap-1.5" data-testid="add-weeks-controls">
              {[1, 2, 4].map(n => (
                <button key={n} onClick={() => addWeeks(n)} data-testid={`add-weeks-${n}`}
                  className="bg-[#0a0a0a] border border-[#d7ff00]/40 text-[#d7ff00] font-mono-jp text-[11px] uppercase tracking-widest px-3 py-2 hover:bg-[#d7ff00]/10 flex items-center gap-1">
                  <Plus className="w-3 h-3" /> {n} wk{n > 1 ? "s" : ""}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#bbb]">
            <input type="checkbox" checked={edit.published} onChange={e => setEdit({ ...edit, published: e.target.checked, status: e.target.checked ? "approved" : "draft" })} className="accent-[#d7ff00]" data-testid="prog-published" />
            Published & approved
          </label>
          <div className="ml-auto flex gap-2">
            <button onClick={save} className="bg-[#d7ff00] text-black font-display uppercase tracking-wider px-5 py-2.5 hover:bg-white flex items-center gap-2" data-testid="prog-save"><Save className="w-4 h-4" /> Save</button>
            <button onClick={() => setEdit(null)} className="bg-[#111] border border-[#222] px-4 py-2.5" data-testid="prog-cancel">Cancel</button>
          </div>
        </div>

        {/* Month nav (4-week pages) */}
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <button onClick={() => setActiveWeek(Math.max(0, currentGroup.start - 4))} disabled={currentGroup.start === 0}
              className="bg-[#0a0a0a] border border-[#222] px-3 py-2 text-[#d7ff00] hover:bg-[#1a1a1a] disabled:opacity-30 flex items-center gap-1" data-testid="month-prev">
              <ChevronLeft className="w-3.5 h-3.5" /> PREV
            </button>
            <span className="font-display text-xl uppercase tracking-wider text-[#d7ff00]" data-testid="month-label">
              Month {monthGroups.findIndex(g => g.start === currentGroup.start) + 1} — Weeks {currentGroup.start + 1}–{currentGroup.end}
            </span>
            <button onClick={() => setActiveWeek(Math.min((edit.weeks || 1) - 1, currentGroup.start + 4))} disabled={currentGroup.end >= edit.weeks}
              className="bg-[#0a0a0a] border border-[#222] px-3 py-2 text-[#d7ff00] hover:bg-[#1a1a1a] disabled:opacity-30 flex items-center gap-1" data-testid="month-next">
              NEXT <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <button onClick={duplicateWeek} className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] border border-[#d7ff00]/40 px-3 py-1.5 hover:bg-[#d7ff00]/10 flex items-center gap-1" data-testid="dup-week">
            <Copy className="w-3 h-3" /> Duplicate W{activeWeek + 1} → W{activeWeek + 2}
          </button>
        </div>

        {/* Week tabs within this month */}
        <div className="mt-4 flex gap-1 overflow-x-auto hide-scrollbar">
          {Array.from({ length: currentGroup.end - currentGroup.start }, (_, i) => {
            const w = currentGroup.start + i;
            const count = edit.sessions.filter(s => s.week_index === w).length;
            return (
              <button key={w} onClick={() => setActiveWeek(w)} data-testid={`week-tab-${w}`}
                className={`flex-shrink-0 px-4 py-2 border ${activeWeek === w ? "border-[#d7ff00] bg-[#d7ff00]/10 text-[#d7ff00]" : "border-[#1f1f1f] text-[#888] hover:text-white"}`}>
                <div className="font-mono-jp text-[10px] uppercase tracking-widest">Week {w + 1}</div>
                <div className="font-mono-jp text-[9px] text-[#666]">{count} sessions</div>
              </button>
            );
          })}
        </div>

        {/* Calendar grid for active week */}
        <div className="mt-4 grid grid-cols-7 gap-2">
          {DAYS.map((d, i) => {
            const s = sessionAt(activeWeek, i);
            const color = s ? (TYPE_COLORS[s.type] || "#d7ff00") : "#222";
            return (
              <div key={d} className="bg-[#0a0a0a] border border-[#1f1f1f] p-3 min-h-[140px] flex flex-col" data-testid={`day-${activeWeek}-${i}`}>
                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center justify-between">
                  <span>{d}</span>
                  {s && <span className="w-2 h-2 rounded-full" style={{ background: color }} />}
                </div>
                {s ? (
                  <>
                    <div className="mt-2 font-display text-sm uppercase truncate" title={s.name}>{s.name}</div>
                    <div className="font-mono-jp text-[9px] text-[#888] uppercase tracking-widest">{s.type}</div>
                    {s.description && <div className="mt-1 text-[10px] text-[#bbb] line-clamp-3">{s.description}</div>}
                    <div className="mt-1 flex flex-wrap gap-1 font-mono-jp text-[9px]">
                      {s.distance_km && <span className="text-[#bbb]">{s.distance_km}km</span>}
                      {s.duration_min && <span className="text-[#bbb]">{s.duration_min}min</span>}
                      {s.hr_zone && <span className="text-[#d7ff00]">{s.hr_zone}</span>}
                    </div>
                    <div className="mt-auto pt-2 flex gap-1">
                      <button onClick={() => openSessionEditor(activeWeek, i)} className="flex-1 font-mono-jp text-[9px] uppercase tracking-widest border border-[#d7ff00]/40 text-[#d7ff00] py-1 hover:bg-[#d7ff00]/10 flex items-center justify-center gap-1" data-testid={`edit-session-${activeWeek}-${i}`}>
                        <Edit3 className="w-3 h-3" /> EDIT
                      </button>
                      <button onClick={() => removeSessionAt(activeWeek, i)} className="font-mono-jp text-[9px] uppercase tracking-widest border border-[#ff3b30]/40 text-[#ff3b30] px-2 py-1 hover:bg-[#ff3b30]/10" data-testid={`del-session-${activeWeek}-${i}`} title="Remove session">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </>
                ) : (
                  <button onClick={() => openSessionEditor(activeWeek, i)} className="mt-auto w-full font-mono-jp text-[10px] uppercase tracking-widest border border-dashed border-[#333] text-[#666] py-3 hover:border-[#d7ff00] hover:text-[#d7ff00] flex items-center justify-center gap-1" data-testid={`add-session-${activeWeek}-${i}`}>
                    <Plus className="w-3 h-3" /> ADD
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Week summary */}
        <div className="mt-4 bg-[#0a0a0a] border border-[#1f1f1f] p-3 font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">
          Week {activeWeek + 1} totals:
          <span className="text-[#d7ff00] ml-2">{edit.sessions.filter(s => s.week_index === activeWeek).reduce((sum, s) => sum + (s.distance_km || 0), 0).toFixed(1)} km</span>
          <span className="ml-3">{edit.sessions.filter(s => s.week_index === activeWeek).length} sessions</span>
        </div>

        {/* Session editor modal */}
        {editSession && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEditSession(null)} data-testid="session-edit-modal">
            <div className="bg-[#0a0a0a] border border-[#d7ff00] w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between mb-3">
                <div>
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">EDIT SESSION · WEEK {editSession.week_index + 1} · {DAYS[editSession.day_index]}</div>
                  <h3 className="font-display text-2xl uppercase mt-1">{editSession.name || "New session"}</h3>
                </div>
                <button onClick={() => setEditSession(null)} className="text-[#888]" data-testid="session-edit-close"><X /></button>
              </div>

              {/* Library quick-pick */}
              {library.length > 0 && (
                <details className="bg-[#0e0e0e] border border-[#222] p-3 mb-4">
                  <summary className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] cursor-pointer">+ Quick-pick from workout library ({library.length})</summary>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                    {library.map(lib => (
                      <button key={lib.id} onClick={() => addFromLibrary(lib)} className="text-left bg-[#070707] border border-[#222] hover:border-[#d7ff00] p-2" data-testid={`lib-pick-${lib.id}`}>
                        <div className="font-display text-xs uppercase truncate">{lib.name}</div>
                        <div className="font-mono-jp text-[9px] text-[#888] uppercase tracking-widest">{lib.type}</div>
                      </button>
                    ))}
                  </div>
                </details>
              )}

              <div className="grid sm:grid-cols-2 gap-3">
                <Lbl label="Name *"><input value={editSession.name || ""} onChange={e => setEditSession({ ...editSession, name: e.target.value })} className={fieldCls} data-testid="se-name" /></Lbl>
                <Lbl label="Type">
                  <select value={editSession.type || "easy"} onChange={e => setEditSession({ ...editSession, type: e.target.value })} className={fieldCls} data-testid="se-type">
                    {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Lbl>
                <Lbl label="Distance (km)"><input type="number" step="0.1" value={editSession.distance_km ?? ""} onChange={e => setEditSession({ ...editSession, distance_km: e.target.value })} className={fieldCls} data-testid="se-distance" /></Lbl>
                <Lbl label="Duration (min)"><input type="number" value={editSession.duration_min ?? ""} onChange={e => setEditSession({ ...editSession, duration_min: e.target.value })} className={fieldCls} data-testid="se-duration" /></Lbl>
                <Lbl label="Pace target"><input value={editSession.pace_target || ""} onChange={e => setEditSession({ ...editSession, pace_target: e.target.value })} placeholder="e.g. 3:50-4:00 min/km" className={fieldCls} data-testid="se-pace" /></Lbl>
                <Lbl label="HR zone"><input value={editSession.hr_zone || ""} onChange={e => setEditSession({ ...editSession, hr_zone: e.target.value })} placeholder="Z2 / Z4 / Z5" className={fieldCls} data-testid="se-hr" /></Lbl>
              </div>
              <Lbl label="Description / Workout detail"><textarea rows={4} value={editSession.description || ""} onChange={e => setEditSession({ ...editSession, description: e.target.value })} placeholder="e.g. 5x1km @ threshold pace, 2min jog recovery between reps. Strides at the end." className={fieldCls} data-testid="se-desc" /></Lbl>

              <div className="mt-4 flex gap-2">
                <button onClick={saveSession} className="flex-1 bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white flex items-center justify-center gap-2" data-testid="se-save">
                  <Save className="w-4 h-4" /> Save session
                </button>
                <button onClick={() => setEditSession(null)} className="px-5 border border-[#222]">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== LIST VIEW =====
  return (
    <div data-testid="admin-programs">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">PROGRAMS</div>
          <h1 className="font-display text-4xl uppercase">Training <span className="text-[#d7ff00]">programs</span></h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setAiOpen(true)} className="bg-[#d7ff00] text-black font-display tracking-wider uppercase px-4 py-2.5 hover:bg-white flex items-center gap-2" data-testid="ai-generate-btn"><Sparkles className="w-4 h-4" /> AI Generate</button>
          <button onClick={startNew} className="border border-[#d7ff00] text-[#d7ff00] font-display tracking-wider uppercase px-4 py-2.5 hover:bg-[#d7ff00]/10 flex items-center gap-2" data-testid="new-prog-btn"><Plus className="w-4 h-4" /> New</button>
        </div>
      </div>

      {/* View tabs */}
      <div className="mt-5 flex gap-2 border-b border-[#1f1f1f]">
        <button
          onClick={() => setView("programs")}
          className={`px-4 py-2.5 font-mono-jp text-[10px] uppercase tracking-widest flex items-center gap-2 border-b-2 -mb-px transition-colors ${view === "programs" ? "border-[#d7ff00] text-[#d7ff00]" : "border-transparent text-[#888] hover:text-white"}`}
          data-testid="tab-programs"
        >
          Programs · {progs.length}
        </button>
        <button
          onClick={() => setView("templates")}
          className={`px-4 py-2.5 font-mono-jp text-[10px] uppercase tracking-widest flex items-center gap-2 border-b-2 -mb-px transition-colors ${view === "templates" ? "border-[#d7ff00] text-[#d7ff00]" : "border-transparent text-[#888] hover:text-white"}`}
          data-testid="tab-templates"
        >
          <LayoutTemplate className="w-3 h-3" /> Templates · {templates.length}
        </button>
      </div>

      {view === "programs" && (
        <div className="mt-4 bg-[#0a0a0a] border border-[#1f1f1f] divide-y divide-[#1f1f1f]">
          {progs.length === 0 && <div className="p-5 text-[#888] text-sm">No programs yet. Use AI Generate to draft one, or create manually.</div>}
          {progs.map(p => {
            const athleteName = athletes.find(a => a.id === p.target_athlete_id)?.name;
            return (
              <div key={p.id} className="p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`prog-row-${p.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-display text-lg uppercase">{p.name}</div>
                    {p.ai_generated && <span className="font-mono-jp text-[9px] uppercase tracking-widest text-[#d7ff00] border border-[#d7ff00]/40 px-1.5">AI</span>}
                    {p.published ? <span className="font-mono-jp text-[9px] uppercase tracking-widest text-[#d7ff00]">PUBLISHED</span> : <span className="font-mono-jp text-[9px] uppercase tracking-widest text-[#ff8800]">DRAFT</span>}
                  </div>
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#666]">
                    {p.goal?.toUpperCase()} · {p.weeks}w · {p.sessions?.length || 0} sessions{athleteName ? ` · for ${athleteName}` : ""}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => startEdit(p)} className="border border-[#d7ff00] text-[#d7ff00] font-mono-jp text-[10px] uppercase tracking-widest px-3 py-1.5 hover:bg-[#d7ff00]/10 flex items-center gap-1" data-testid={`edit-${p.id}`}>
                    <Edit3 className="w-3 h-3" /> Review & edit
                  </button>
                  <button onClick={() => saveAsTemplate(p)} title="Save this program as a reusable template"
                    className="border border-[#888] text-[#bbb] font-mono-jp text-[10px] uppercase tracking-widest px-3 py-1.5 hover:border-[#d7ff00] hover:text-[#d7ff00] flex items-center gap-1" data-testid={`save-template-${p.id}`}>
                    <BookmarkPlus className="w-3 h-3" /> Save as template
                  </button>
                  <button onClick={() => openAssign(p)} disabled={!p.published} title={!p.published ? "Publish first" : ""}
                    className={`font-mono-jp text-[10px] uppercase tracking-widest px-3 py-1.5 flex items-center gap-1 ${p.published ? "bg-[#d7ff00] text-black hover:bg-white" : "bg-[#222] text-[#666] cursor-not-allowed"}`} data-testid={`assign-${p.id}`}>
                    <UserPlus className="w-3 h-3" /> Assign
                  </button>
                  <button onClick={() => remove(p.id)} className="text-[#ff3b30] hover:text-red-300 p-1.5" data-testid={`del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "templates" && (
        <div className="mt-4 bg-[#0a0a0a] border border-[#1f1f1f] divide-y divide-[#1f1f1f]" data-testid="templates-list">
          {templates.length === 0 && (
            <div className="p-6 text-center">
              <LayoutTemplate className="w-8 h-8 text-[#444] mx-auto mb-3" />
              <div className="text-[#888] text-sm">No templates saved yet.</div>
              <div className="text-[#666] text-xs mt-1">Open any program → click <span className="text-[#d7ff00]">Save as template</span> to reuse it for future athletes.</div>
            </div>
          )}
          {templates.map(t => (
            <div key={t.id} className="p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`template-row-${t.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <LayoutTemplate className="w-4 h-4 text-[#d7ff00]" />
                  <div className="font-display text-lg uppercase">{t.name}</div>
                  {t.ai_generated && <span className="font-mono-jp text-[9px] uppercase tracking-widest text-[#d7ff00] border border-[#d7ff00]/40 px-1.5">AI</span>}
                </div>
                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#666]">
                  {t.goal?.toUpperCase()} · {t.weeks}w · {t.sessions?.length || 0} sessions
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => startEdit(t)} className="border border-[#888] text-[#bbb] font-mono-jp text-[10px] uppercase tracking-widest px-3 py-1.5 hover:border-[#d7ff00] hover:text-[#d7ff00] flex items-center gap-1" data-testid={`edit-template-${t.id}`}>
                  <Edit3 className="w-3 h-3" /> Edit template
                </button>
                <button onClick={() => openTemplateUse(t)} className="bg-[#d7ff00] text-black font-mono-jp text-[10px] uppercase tracking-widest px-3 py-1.5 hover:bg-white flex items-center gap-1" data-testid={`use-template-${t.id}`}>
                  <UserPlus className="w-3 h-3" /> Use template
                </button>
                <button onClick={() => deleteTemplate(t.id)} className="text-[#ff3b30] hover:text-red-300 p-1.5" data-testid={`del-template-${t.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI modal */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => !aiBusy && setAiOpen(false)} data-testid="ai-modal">
          <div className="bg-[#0a0a0a] border border-[#d7ff00] w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between mb-3">
              <div>
                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI PROGRAM GENERATOR</div>
                <h3 className="font-display text-2xl uppercase">Generate a draft</h3>
              </div>
              {!aiBusy && <button onClick={() => setAiOpen(false)} className="text-[#888]"><X /></button>}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Lbl label="Tailor for athlete (optional)">
                <select value={aiForm.athlete_id} onChange={e => {
                  const a = athletes.find(x => x.id === e.target.value);
                  setAiForm({ ...aiForm, athlete_id: e.target.value, goal: a ? (a.goal || a.goal_race || aiForm.goal) : aiForm.goal });
                }} className={fieldCls} data-testid="ai-athlete">
                  <option value="">— Generic program —</option>
                  {athletes.map(a => <option key={a.id} value={a.id}>{a.name} ({a.goal_race || a.goal || "no goal"})</option>)}
                </select>
              </Lbl>
              <Lbl label="Goal">
                <select value={aiForm.goal} onChange={e => setAiForm({ ...aiForm, goal: e.target.value })} className={fieldCls} data-testid="ai-goal">
                  {["beginner","5k","10k","half","marathon"].map(g => <option key={g} value={g}>{g.toUpperCase()}</option>)}
                </select>
              </Lbl>
              <Lbl label="Total weeks">
                <select value={aiForm.weeks} onChange={e => setAiForm({ ...aiForm, weeks: Number(e.target.value) })} className={fieldCls} data-testid="ai-weeks">
                  {[4, 6, 8, 10, 12, 16].map(w => <option key={w} value={w}>{w} weeks</option>)}
                </select>
              </Lbl>
              <Lbl label="Training days / week">
                <input type="number" min={1} max={7} value={aiForm.weekly_availability_days} onChange={e => setAiForm({ ...aiForm, weekly_availability_days: Number(e.target.value) })} className={fieldCls} data-testid="ai-days" />
              </Lbl>
              <Lbl label="Target peak weekly mileage (km)">
                <input type="number" value={aiForm.weekly_mileage_target_km} onChange={e => setAiForm({ ...aiForm, weekly_mileage_target_km: e.target.value })} placeholder="e.g. 60" className={fieldCls} data-testid="ai-mileage" />
              </Lbl>
              <Lbl label="Pace zones (optional)">
                <input value={aiForm.pace_zones} onChange={e => setAiForm({ ...aiForm, pace_zones: e.target.value })} placeholder="e.g. Easy 5:30-6:00, Thresh 3:50-4:00" className={fieldCls} data-testid="ai-paces" />
              </Lbl>
            </div>
            <Lbl label="Required session types (multi-select)">
              <div className="flex flex-wrap gap-1.5 mt-2">
                {TYPES.map(t => {
                  const has = aiForm.required_session_types.includes(t);
                  return (
                    <button key={t} type="button" onClick={() => setAiForm(f => ({ ...f, required_session_types: has ? f.required_session_types.filter(x => x !== t) : [...f.required_session_types, t] }))}
                      className={`px-2 py-1 font-mono-jp text-[9px] uppercase tracking-widest border ${has ? "border-[#d7ff00] text-[#d7ff00] bg-[#d7ff00]/10" : "border-[#222] text-[#bbb]"}`} data-testid={`ai-type-${t}`}>{t}</button>
                  );
                })}
              </div>
            </Lbl>
            <Lbl label="Coach guidance / specific instructions"><textarea rows={3} value={aiForm.coach_guidance} onChange={e => setAiForm({ ...aiForm, coach_guidance: e.target.value })} placeholder="e.g. Long runs Sunday only. Include strides Tuesdays. Avoid hard sessions after rest day." className={fieldCls} data-testid="ai-guidance" /></Lbl>
            <Lbl label="Additional notes"><textarea rows={2} value={aiForm.notes} onChange={e => setAiForm({ ...aiForm, notes: e.target.value })} className={fieldCls} data-testid="ai-notes" /></Lbl>

            {aiForm.athlete_id && (
              <label className="mt-4 flex items-start gap-3 bg-[#1a1f00] border border-[#d7ff00] p-3 cursor-pointer" data-testid="ai-1click-toggle">
                <input type="checkbox" checked={aiForm.auto_publish_and_assign} onChange={e => setAiForm({ ...aiForm, auto_publish_and_assign: e.target.checked })} className="mt-0.5 accent-[#d7ff00]" />
                <div>
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">⚡ 1-CLICK SEND</div>
                  <div className="text-xs text-[#bbb] mt-1">Generate + publish + assign to <span className="text-white">{selectedAthlete?.name || "athlete"}</span> in one shot. Skip the manual review (you can still edit later from their athlete page).</div>
                </div>
              </label>
            )}

            <button onClick={generateAI} disabled={aiBusy} className="mt-4 w-full bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white disabled:opacity-50 flex items-center justify-center gap-2" data-testid="ai-generate-confirm">
              <Sparkles className="w-4 h-4" /> {aiBusy ? "Claude is drafting your program… (10-30s)" : (aiForm.auto_publish_and_assign ? "Generate & send to athlete" : "Generate draft")}
            </button>
            <p className="mt-2 text-[10px] font-mono-jp text-[#666] uppercase tracking-widest text-center">{aiForm.auto_publish_and_assign ? "1-click: athlete gets it instantly." : "After generation: review, edit any session, then tick PUBLISHED and Assign."}</p>
          </div>
        </div>
      )}

      {/* Assign modal */}
      {assignFor && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => !assignBusy && setAssignFor(null)} data-testid="assign-modal">
          <div className="bg-[#0a0a0a] border border-[#d7ff00] w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-xl uppercase">Assign · {assignFor.name}</h3>
            <select value={assignAthleteId} onChange={e => setAssignAthleteId(e.target.value)} className={`${fieldCls} mt-4`} data-testid="assign-athlete-select">
              <option value="">— Select athlete —</option>
              {athletes.map(a => <option key={a.id} value={a.id}>{a.name} ({a.email})</option>)}
            </select>
            <button onClick={doAssign} disabled={assignBusy || !assignAthleteId} className="mt-4 w-full bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white disabled:opacity-50 flex items-center justify-center gap-2" data-testid="assign-confirm">
              <CheckCircle2 className="w-4 h-4" /> {assignBusy ? "Assigning…" : "Send to athlete"}
            </button>
            <button onClick={() => setAssignFor(null)} className="mt-2 w-full text-xs text-[#888]">Cancel</button>
          </div>
        </div>
      )}

      {/* Use Template modal */}
      {tplAssignFor && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => !tplAssignBusy && setTplAssignFor(null)} data-testid="use-template-modal">
          <div className="bg-[#0a0a0a] border border-[#d7ff00] w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-1">
                  <LayoutTemplate className="w-3 h-3" /> USE TEMPLATE
                </div>
                <h3 className="font-display text-xl uppercase mt-1">{tplAssignFor.name}</h3>
                <p className="text-[10px] font-mono-jp text-[#666] uppercase tracking-widest mt-1">{tplAssignFor.goal?.toUpperCase()} · {tplAssignFor.weeks}w · {tplAssignFor.sessions?.length || 0} sessions</p>
              </div>
              {!tplAssignBusy && <button onClick={() => setTplAssignFor(null)} className="text-[#888]" data-testid="use-template-close"><X /></button>}
            </div>

            <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Assign to athlete (optional)</label>
            <select value={tplAssignAthleteId} onChange={e => setTplAssignAthleteId(e.target.value)} className={`${fieldCls} mt-1`} data-testid="use-template-athlete">
              <option value="">— Clone to drafts only (review first) —</option>
              {athletes.map(a => <option key={a.id} value={a.id}>{a.name} ({a.goal_race || a.goal || "no goal"})</option>)}
            </select>

            <p className="mt-3 text-[11px] text-[#888]">
              {tplAssignAthleteId
                ? "Clones this template into a new program, publishes it, and assigns it directly to the athlete."
                : "Clones this template into a new draft program you can review & edit before publishing."}
            </p>

            <button onClick={cloneTemplate} disabled={tplAssignBusy} className="mt-4 w-full bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white disabled:opacity-50 flex items-center justify-center gap-2" data-testid="use-template-confirm">
              <CheckCircle2 className="w-4 h-4" /> {tplAssignBusy ? "Cloning…" : (tplAssignAthleteId ? "Clone & Assign" : "Clone to drafts")}
            </button>
            <button onClick={() => setTplAssignFor(null)} className="mt-2 w-full text-xs text-[#888]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

const fieldCls = "w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2 text-sm";
function Lbl({ label, children }) {
  return (
    <div className="mt-3">
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
